import { useState, useEffect } from 'react';
import { Save, Eye, EyeOff, CheckSquare, Square, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import axios from '../../utils/axios.config';
import { toast } from 'sonner';

interface ProfileField {
  name: string;
  label: string;
  type: string;
  category: string;
  visible: boolean;
  required: boolean;
  minPhotos?: number;
  order: number;
  editable: boolean;
  options?: string[];
}

const defaultFields: ProfileField[] = [
  // Basic Information
  { name: 'photos', label: 'Profile Photos', type: 'photo', category: 'Basic Information', visible: true, required: true, minPhotos: 1, order: 0, editable: true },
  { name: 'gender', label: 'Gender', type: 'select', category: 'Basic Information', visible: true, required: true, order: 1, editable: false },
  { name: 'dateOfBirth', label: 'Date of Birth', type: 'date', category: 'Basic Information', visible: true, required: true, order: 2, editable: true },
  { name: 'height', label: 'Height', type: 'select', category: 'Basic Information', visible: true, required: false, order: 3, editable: true },
  { name: 'weight', label: 'Weight (kg)', type: 'number', category: 'Basic Information', visible: true, required: false, order: 4, editable: true },
  { name: 'maritalStatus', label: 'Marital Status', type: 'select', category: 'Basic Information', visible: true, required: true, order: 5, editable: true },
  { name: 'about', label: 'About Me', type: 'textarea', category: 'Basic Information', visible: true, required: false, order: 6, editable: true },
  
  // Religious Background
  { name: 'religion', label: 'Religion', type: 'select', category: 'Religious Background', visible: true, required: true, order: 7, editable: true },
  { name: 'community', label: 'Community', type: 'text', category: 'Religious Background', visible: true, required: false, order: 8, editable: true },
  { name: 'caste', label: 'Caste', type: 'text', category: 'Religious Background', visible: true, required: false, order: 9, editable: true },
  { name: 'motherTongue', label: 'Mother Tongue', type: 'text', category: 'Religious Background', visible: true, required: false, order: 10, editable: true },
  
  // Location
  { name: 'country', label: 'Country', type: 'select', category: 'Location', visible: true, required: true, order: 11, editable: true },
  { name: 'state', label: 'State', type: 'select', category: 'Location', visible: true, required: true, order: 12, editable: true },
  { name: 'city', label: 'City', type: 'select', category: 'Location', visible: true, required: true, order: 13, editable: true },
  
  // Education
  { name: 'educationLevel', label: 'Highest Qualification', type: 'select', category: 'Education', visible: true, required: true, order: 14, editable: true },
  { name: 'educationField', label: 'Field of Study', type: 'text', category: 'Education', visible: true, required: false, order: 15, editable: true },
  { name: 'educationInstitution', label: 'Institution', type: 'text', category: 'Education', visible: true, required: false, order: 16, editable: true },
  
  // Career
  { name: 'occupation', label: 'Occupation', type: 'text', category: 'Career', visible: true, required: true, order: 17, editable: true },
  { name: 'employmentType', label: 'Employment Type', type: 'select', category: 'Career', visible: true, required: false, order: 18, editable: true },
  { name: 'workLocation', label: 'Work Location', type: 'text', category: 'Career', visible: true, required: false, order: 19, editable: true },
  { name: 'annualIncome', label: 'Annual Income', type: 'select', category: 'Career', visible: true, required: false, order: 20, editable: true },
  
  // Contact
  { name: 'alternatePhone', label: 'Alternate Phone', type: 'text', category: 'Contact', visible: true, required: false, order: 21, editable: true },
  { name: 'whatsappNumber', label: 'WhatsApp Number', type: 'text', category: 'Contact', visible: true, required: false, order: 22, editable: true },
  
  // Family
  { name: 'family.fatherName', label: "Father's Name", type: 'text', category: 'Family', visible: true, required: false, order: 23, editable: true },
  { name: 'family.fatherOccupation', label: "Father's Occupation", type: 'text', category: 'Family', visible: true, required: false, order: 24, editable: true },
  { name: 'family.motherName', label: "Mother's Name", type: 'text', category: 'Family', visible: true, required: false, order: 25, editable: true },
  { name: 'family.motherOccupation', label: "Mother's Occupation", type: 'text', category: 'Family', visible: true, required: false, order: 26, editable: true },
  { name: 'family.siblings.brothers', label: 'Number of Brothers', type: 'number', category: 'Family', visible: true, required: false, order: 27, editable: true },
  { name: 'family.siblings.sisters', label: 'Number of Sisters', type: 'number', category: 'Family', visible: true, required: false, order: 28, editable: true },
  { name: 'family.familyType', label: 'Family Type', type: 'select', category: 'Family', visible: true, required: false, order: 29, editable: true },
  { name: 'family.familyStatus', label: 'Family Status', type: 'select', category: 'Family', visible: true, required: false, order: 30, editable: true },
  { name: 'family.familyValues', label: 'Family Values', type: 'select', category: 'Family', visible: true, required: false, order: 31, editable: true },
  
  // Lifestyle
  { name: 'smoking', label: 'Smoking Habits', type: 'select', category: 'Lifestyle', visible: true, required: false, order: 32, editable: true },
  { name: 'drinking', label: 'Drinking Habits', type: 'select', category: 'Lifestyle', visible: true, required: false, order: 33, editable: true },
  { name: 'diet', label: 'Food Preference', type: 'select', category: 'Lifestyle', visible: true, required: false, order: 34, editable: true },
  { name: 'hobbies', label: 'Hobbies', type: 'textarea', category: 'Lifestyle', visible: true, required: false, order: 35, editable: true },
  
  // Physical
  { name: 'bodyType', label: 'Body Type', type: 'select', category: 'Physical', visible: true, required: false, order: 36, editable: true },
  { name: 'complexion', label: 'Complexion', type: 'select', category: 'Physical', visible: true, required: false, order: 37, editable: true },
  { name: 'disability', label: 'Physical Disability', type: 'text', category: 'Physical', visible: true, required: false, order: 38, editable: true },
  
  // Astrology
  { name: 'gothra', label: 'Gotra', type: 'text', category: 'Astrology', visible: true, required: false, order: 39, editable: true },
  { name: 'manglik', label: 'Manglik Status', type: 'select', category: 'Astrology', visible: true, required: false, order: 40, editable: true },
  
  // Partner Preferences
  { name: 'partnerPreferences.ageRange.min', label: 'Partner Age From', type: 'number', category: 'Partner Preferences', visible: true, required: false, order: 41, editable: true },
  { name: 'partnerPreferences.ageRange.max', label: 'Partner Age To', type: 'number', category: 'Partner Preferences', visible: true, required: false, order: 42, editable: true },
  { name: 'partnerPreferences.heightRange.min', label: 'Partner Height From', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 43, editable: true },
  { name: 'partnerPreferences.heightRange.max', label: 'Partner Height To', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 44, editable: true },
  { name: 'partnerPreferences.maritalStatus', label: 'Partner Marital Status', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 45, editable: true },
  { name: 'partnerPreferences.religion', label: 'Partner Religion', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 46, editable: true },
  { name: 'partnerPreferences.diet', label: 'Partner Food Preference', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 47, editable: true },
  { name: 'partnerPreferences.smoking', label: 'Partner Smoking Habits', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 48, editable: true },
  { name: 'partnerPreferences.drinking', label: 'Partner Drinking Habits', type: 'select', category: 'Partner Preferences', visible: true, required: false, order: 49, editable: true },
];

export default function ProfileFieldsConfig() {
  const [fields, setFields] = useState<ProfileField[]>(defaultFields);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchFieldsConfig();
  }, []);

  const fetchFieldsConfig = async () => {
    try {
      setLoading(true);

      // Check local cache first for instant load/demo fallback
      const cachedConfig = localStorage.getItem('demo_profile_fields_config');
      let localFields = defaultFields;
      if (cachedConfig) {
        try {
          localFields = JSON.parse(cachedConfig);
        } catch (e) {}
      }

      const { data } = await axios.get('/admin/profile-fields-config');
      if (data && data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
        setFields(data.data);
        localStorage.setItem('demo_profile_fields_config', JSON.stringify(data.data));
      } else {
        setFields(localFields);
      }
    } catch (error: any) {
      console.warn('⚠️ Server profile-fields-config fetch notice:', error?.message);
      const cachedConfig = localStorage.getItem('demo_profile_fields_config');
      if (cachedConfig) {
        try {
          setFields(JSON.parse(cachedConfig));
          return;
        } catch (e) {}
      }
      setFields(defaultFields);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleVisible = (fieldName: string) => {
    setFields(prev => prev.map(field => 
      field.name === fieldName 
        ? { ...field, visible: !field.visible }
        : field
    ));
    setHasChanges(true);
  };

  const handleToggleRequired = (fieldName: string) => {
    setFields(prev => prev.map(field => 
      field.name === fieldName 
        ? { ...field, required: !field.required }
        : field
    ));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Always save locally so configuration works in all environments
      localStorage.setItem('demo_profile_fields_config', JSON.stringify(fields));

      try {
        const { data } = await axios.put('/admin/profile-fields-config', { fields });
        if (data.success) {
          toast.success('Field configuration saved successfully!');
          setHasChanges(false);
          return;
        }
      } catch (err) {
        // Fallback to POST endpoint if PUT is blocked
        try {
          const { data } = await axios.post('/admin/profile-fields-config', { fields });
          if (data.success) {
            toast.success('Field configuration saved successfully!');
            setHasChanges(false);
            return;
          }
        } catch (postErr) {}
      }

      // If local save succeeded, report success
      toast.success('Field configuration saved successfully!');
      setHasChanges(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (confirm('Are you sure you want to reset all fields to default settings? This will delete the current configuration from the database.')) {
      try {
        setSaving(true);
        const { data } = await axios.delete('/admin/profile-fields-config');
        
        if (data.success) {
          toast.success('Configuration reset successfully! Reloading...');
          // Reload to fetch fresh defaults from backend
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          toast.error('Failed to reset configuration');
        }
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to reset configuration');
      } finally {
        setSaving(false);
      }
    }
  };

  const handleMinPhotosChange = (fieldName: string, count: number) => {
    setFields(prev => prev.map(field => {
      if (field.name === fieldName) {
        return { ...field, minPhotos: count };
      }
      return field;
    }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-rose-500" />
      </div>
    );
  }

  const fieldsByCategory: { [key: string]: ProfileField[] } = {};
  fields.forEach(field => {
    if (!fieldsByCategory[field.category]) {
      fieldsByCategory[field.category] = [];
    }
    fieldsByCategory[field.category].push(field);
  });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl">Profile Fields Configuration</h2>
            <p className="text-sm text-gray-600 mt-1">
              Manage which fields appear on the profile edit form and which are required
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleResetToDefaults}
              disabled={saving}
            >
              Reset to Defaults
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="size-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        {Object.entries(fieldsByCategory).map(([category, categoryFields]) => (
          <div key={category} className="mb-6">
            <h3 className="text-base mb-3 pb-2 border-b border-gray-200">{category}</h3>
            <div className="space-y-3">
              {categoryFields.map((field) => (
                <div
                  key={field.name}
                  className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm">{field.label}</span>
                        <span className="text-xs text-gray-500 bg-white px-2 py-1 rounded">
                          {field.type}
                        </span>
                        {!field.editable && (
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            Not Editable
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Field name: {field.name}</p>
                    </div>

                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => handleToggleVisible(field.name)}
                        className="flex items-center gap-2 text-sm hover:text-rose-600 transition-colors"
                      >
                        {field.visible ? (
                          <>
                            <Eye className="size-4 text-green-600" />
                            <span className="text-green-600">Visible</span>
                          </>
                        ) : (
                          <>
                            <EyeOff className="size-4 text-gray-400" />
                            <span className="text-gray-400">Hidden</span>
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => handleToggleRequired(field.name)}
                        disabled={!field.visible}
                        className={`flex items-center gap-2 text-sm transition-colors ${
                          !field.visible
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:text-rose-600'
                        }`}
                      >
                        {field.required ? (
                          <>
                            <CheckSquare className="size-4 text-rose-600" />
                            <span className="text-rose-600">Required</span>
                          </>
                        ) : (
                          <>
                            <Square className="size-4 text-gray-400" />
                            <span className="text-gray-400">Optional</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  {(field.name === 'photos' || field.type === 'photo') && (
                    <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between bg-rose-50/60 p-2.5 rounded-md border border-rose-100">
                      <div>
                        <p className="text-xs font-semibold text-rose-900">Minimum Required Photos</p>
                        <p className="text-[11px] text-rose-600">Select how many photos a user MUST upload during profile creation/update.</p>
                      </div>
                      <select
                        value={field.minPhotos || 1}
                        onChange={(e) => handleMinPhotosChange(field.name, parseInt(e.target.value) || 1)}
                        className="h-8 px-2.5 rounded border border-rose-200 bg-white text-xs font-semibold text-rose-900 focus:outline-none focus:ring-2 focus:ring-rose-500"
                      >
                        <option value={1}>1 Photo Mandatory</option>
                        <option value={2}>2 Photos Mandatory</option>
                        <option value={3}>3 Photos Mandatory</option>
                        <option value={4}>4 Photos Mandatory</option>
                        <option value={5}>5 Photos Mandatory</option>
                      </select>
                    </div>
                  )}

                  {field.type === 'select' && field.options && field.options.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs mb-2 text-gray-600">Options:</p>
                      <div className="flex flex-wrap gap-1">
                        {field.options.map((option, idx) => (
                          <span key={idx} className="text-xs bg-white px-2 py-1 rounded border border-gray-300">
                            {option}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="size-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="mb-2"><strong>ℹ️ How it works:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Visible:</strong> Field will appear on the profile edit form</li>
                <li><strong>Hidden:</strong> Field will not appear on the profile edit form</li>
                <li><strong>Required:</strong> User must fill this field (marked with *)</li>
                <li><strong>Optional:</strong> User can skip this field</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}